import DashboardDefaultChartEmptyState from "@components/EmptyStates/DashboardDefaultChartEmptyState";
import { LawsuitTrackerLineSkeleton } from "@components/Loaders/skeletons";
import { CustomChartStyleWrapper, ToggletipText } from "src/style";
import React, { useState } from "react";
import ReactApexChart from "react-apexcharts";
import { sendEventChartUsed } from "@services/dashboardAnalyticEventService";
import { dashboardChartFilterAndActionTypes } from "@utils/constants/dashboard/analyticDashboardEvents";
import {
  DASHBOARD_CASE_TYPES,
  DASHBOARD_CHART_TYPES,
  DATE_RANGE_DEFAULT_HISTOGRAM_KEY_TYPES,
} from "@utils/constants/dashboard/dashboardModuleConstants";

import DashboardChartFilters from "@components/DashboardChartFilters";
import DashboardChartCaseTypeFilter from "@components/DashboardChartFilters/DashboardChartCaseTypeFilter";
import DashboardChartRangeFilter from "@components/DashboardChartFilters/DashboardChartRangeFilter";
import DashboardChartWrapper from "@components/DashboardChartWrapper";
import { QueryLoaderWrapper } from "@components/Loaders";
import { getChartOptions } from "./helpers";
import useLawsuitTrackerLineChartQuery from "./useLawsuitTrackerLineChartQuery";
import { CaseTypeFilter } from "@customTypes/components/filters";

const LawsuitTrackerLineChart = () => {
  const ALLOWED_RANGE_TYPES = [
    "LAST_7_DAYS",
    "LAST_30_DAYS",
    "LAST_90_DAYS",
    "LAST_YEAR",
  ] as DATE_RANGE_DEFAULT_HISTOGRAM_KEY_TYPES[];

  const [caseTypeFilter, setCaseTypeFilter] = useState<CaseTypeFilter>(DASHBOARD_CASE_TYPES.ALL);
  const [rangeFilter, setRangeFilter] =
    useState<DATE_RANGE_DEFAULT_HISTOGRAM_KEY_TYPES>("LAST_30_DAYS");

  const [queryStatus, lawsuitTrackerLineChartData] = useLawsuitTrackerLineChartQuery(
    rangeFilter,
    caseTypeFilter.queryParam
  );
  const { xAxis, series, groups, noData } = lawsuitTrackerLineChartData;
  const options = getChartOptions(xAxis, groups, rangeFilter);
  const CHART_HEIGHT = 400;

  const shouldRenderEmptyState = noData;

  const setRangeFilterAndSendMixPanelEvent = (filter: DATE_RANGE_DEFAULT_HISTOGRAM_KEY_TYPES) => {
    setRangeFilter(filter);
    sendEventChartUsed(
      DASHBOARD_CHART_TYPES.LAWSUIT_TRACKER,
      dashboardChartFilterAndActionTypes.DATE_RANGE,
      filter
    );
  };

  const setCaseTypeFilterAndSendMixPanelEvent = (filter: CaseTypeFilter) => {
    setCaseTypeFilter(filter);
    sendEventChartUsed(
      DASHBOARD_CHART_TYPES.LAWSUIT_TRACKER,
      dashboardChartFilterAndActionTypes.LAWSUIT_TYPE,
      filter
    );
  };

  const header = {
    title: "Processos e casos cadastrados e encerrados",
    description:
      "Confira a quantidade de processos e casos cadastrados e encerrados no período selecionado para analisar a sazonalidade de demandas no escritório.",
  };

  const toggleTip = {
    title: "Processos e casos cadastrados e encerrados",
    text: (
      <ToggletipText>
        São considerados os processos e casos que foram cadastrados ou encerrados no Astrea dentro
        do intervalo indicado no gráfico.
      </ToggletipText>
    ),
    onClickCallback: () => {
      sendEventChartUsed(
        DASHBOARD_CHART_TYPES.LAWSUIT_TRACKER,
        dashboardChartFilterAndActionTypes.TOGGLE_TIP_OPENED
      );
    },
    callToAction: {
      text: "Acessar central de ajuda",
      callback: () => {
        sendEventChartUsed(
          DASHBOARD_CHART_TYPES.LAWSUIT_TRACKER,
          dashboardChartFilterAndActionTypes.HELP_CENTER_CLICK
        );
        window.open(
          "https://astrea.aurum.com.br/pt-BR/articles/8043393-como-analisar-os-processos-e-casos-cadastrados-e-encerrados",
          "_blank"
        );
      },
    },
  };

  return (
    <DashboardChartWrapper
      title={header.title}
      description={header.description}
      toggleTip={toggleTip}
    >
      <DashboardChartFilters>
        <DashboardChartRangeFilter
          allowedRangeTypes={ALLOWED_RANGE_TYPES}
          selectedRange={rangeFilter}
          setRangeFunction={setRangeFilterAndSendMixPanelEvent}
          enableTooltip
        />
        <DashboardChartCaseTypeFilter
          currentFilter={caseTypeFilter}
          setFilter={setCaseTypeFilterAndSendMixPanelEvent}
        />
      </DashboardChartFilters>
      {shouldRenderEmptyState ? (
        <DashboardDefaultChartEmptyState
          title="Nenhum processo ou caso encontrado"
          subtitle="Verifique os filtros selecionados."
          chartId={DASHBOARD_CHART_TYPES.LAWSUIT_TRACKER}
          link="https://astrea.aurum.com.br/pt-BR/articles/8043393-como-analisar-os-processos-e-casos-cadastrados-e-encerrados"
        />
      ) : (
        <CustomChartStyleWrapper avoidStretchMarkersWhenBreakingLine>
          <QueryLoaderWrapper
            queryStatus={queryStatus}
            spinnerConfig={{ height: CHART_HEIGHT }}
            skeletonComponent={<LawsuitTrackerLineSkeleton height={CHART_HEIGHT} />}
          >
            <ReactApexChart
              options={options}
              series={series}
              type="line"
              height={CHART_HEIGHT}
              width="95%"
            />
          </QueryLoaderWrapper>
        </CustomChartStyleWrapper>
      )}
    </DashboardChartWrapper>
  );
};

export default LawsuitTrackerLineChart;
